import React, { useEffect, useState } from 'react';
import { LineSuggestion } from '../types';
import { useLineSuggestions } from '../hooks/useLineSuggestions';
import { SUGGESTION_DEBOUNCE_MS } from '../utils/tagSuggestionLookup';

const LINE_SUGGESTION_MINIMUM_QUERY_LENGTH = 2;
/** A row names this many drawings, then counts the rest. */
const LINE_SUGGESTION_DRAWINGS_SHOWN = 3;

/** "on 1T4031 PR/5AE-9, 20A", with the drawings past the third counted as "+N more". */
const drawingsLabel = (drawings: string[]): string => {
  const shown = drawings.slice(0, LINE_SUGGESTION_DRAWINGS_SHOWN).join(', ');
  const hiddenCount = drawings.length - LINE_SUGGESTION_DRAWINGS_SHOWN;
  return hiddenCount > 0 ? `on ${shown} +${hiddenCount} more` : `on ${shown}`;
};

/**
 * The Line Number input. Once the SME types two characters it lists the line numbers the
 * plant's drawings carry; a pick writes that spelling, and typed text stays as typed.
 */
const LineNumberField: React.FC<{
  docId: string;
  value: string;
  onChange: (lineNo: string) => void;
  onSave: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
}> = ({ docId, value, onChange, onSave, inputRef }) => {
  // Only typing looks the plant up, so a form that opens on a stored line number lists nothing.
  const [typed, setTyped] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const listId = React.useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  // The plant is asked once the typing pauses; a newer query cancels the one in flight.
  const query = typed.trim();
  const [askedQuery, setAskedQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setAskedQuery(query), SUGGESTION_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);
  const lookup = useLineSuggestions(docId, askedQuery, askedQuery.length >= LINE_SUGGESTION_MINIMUM_QUERY_LENGTH);
  useEffect(() => {
    // A failed lookup leaves a plain text field, which still saves.
    if (lookup.error) console.warn('Line Number suggestions failed', lookup.error);
  }, [lookup.error]);

  const suggestions: LineSuggestion[] = askedQuery === query && query.length >= LINE_SUGGESTION_MINIMUM_QUERY_LENGTH
    ? lookup.data ?? []
    : [];
  const listShown = listOpen && suggestions.length > 0;
  const activeSuggestion = listShown ? suggestions[activeIndex] : undefined;
  const activeOptionId = activeSuggestion ? optionId(activeIndex) : undefined;
  // The keyboard moves the active row; the list follows it, so Enter never takes a row out of view.
  useEffect(() => {
    if (activeOptionId) document.getElementById(activeOptionId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeOptionId]);

  const closeList = () => {
    setListOpen(false);
    setActiveIndex(-1);
    setHoveredIndex(-1);
  };
  const pick = (suggestion: LineSuggestion) => {
    onChange(suggestion.line_no);
    closeList();
  };

  /**
   * DECISION(2026-10-26, Abhishek): Enter takes the suggestion the arrow keys made active, and with none it saves the form.
   * An open list that only shows, or a row under a resting pointer, does not hold back the SME who typed the whole number.
   */
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      // An open list takes the first Escape, so the dock closes on the second.
      if (!listShown) return;
      event.preventDefault();
      closeList();
      return;
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && listShown) {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((index) => Math.min(Math.max(index + step, 0), suggestions.length - 1));
      return;
    }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (activeSuggestion) pick(activeSuggestion);
    else onSave();
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        role="combobox"
        aria-label="Line Number"
        aria-expanded={listShown}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        autoComplete="off"
        className="pnid-field"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setTyped(e.target.value);
          setListOpen(true);
          setActiveIndex(-1);
        }}
        onBlur={closeList}
        onKeyDown={onKeyDown}
        placeholder="Line number"
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      {listShown && (
        <div
          id={listId}
          role="listbox"
          aria-label="Line Number suggestions"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 1000,
            background: 'var(--raven-surface-tier1)', border: '1px solid var(--raven-divider)',
            borderRadius: 'var(--raven-radius-menu)', boxShadow: 'var(--raven-shadow-raised)',
            maxHeight: 220, overflowY: 'auto',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.line_no}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              // The field keeps the focus, so its blur does not close the list under the click.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(suggestion)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(-1)}
              style={{
                padding: '6px 10px', cursor: 'pointer', fontSize: 12,
                background: index === activeIndex || index === hoveredIndex ? 'var(--raven-state-hover)' : 'transparent',
              }}
            >
              <div>{suggestion.line_no}</div>
              <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)' }}>{drawingsLabel(suggestion.drawings)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LineNumberField;
